const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY

const numberFormat = new Intl.NumberFormat()
const dateFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
})

/**
 * Compact "when" label for timestamps. Falls back to an absolute date past a
 * month, where "5w ago" stops being easier to read than the date itself.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'Never'

  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'Never'

  const elapsed = Date.now() - then
  // Clock skew between server and browser can put a fresh timestamp in the future.
  if (elapsed < 45_000) return 'just now'
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`
  if (elapsed < WEEK) return `${Math.floor(elapsed / DAY)}d ago`
  if (elapsed < 5 * WEEK) return `${Math.floor(elapsed / WEEK)}w ago`

  return dateFormat.format(then)
}

export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '0'
  return numberFormat.format(n)
}

/**
 * Returns the noun alone — the caller owns the count so it can be formatted:
 * `${formatNumber(n)} ${pluralize(n, 'image')}`.
 */
export function pluralize(n: number, one: string, many?: string): string {
  if (n === 1 || n === -1) return one
  return many ?? `${one}s`
}

export function truncate(s: string, n: number): string {
  if (n <= 0) return ''
  if (s.length <= n) return s
  return `${s.slice(0, n - 1).trimEnd()}…`
}
